import {
  components,
  components as mediaComponents,
} from "@libertify/api-types/media";
import { Maybe } from "types";
export type Prompt = {
  id: string;
  prompt: string;
};
export type Completion = {
  id: string;
  promptId: string;
  completion: string;
};
export type Message = Prompt | Completion;

export type MediaAsset = mediaComponents["schemas"]["MediaAsset"];
export type MediaAssetType = mediaComponents["schemas"]["MediaAssetType"];
export type StoryboardAsset = MediaAsset & { asset_id: string };
export type SlidesAsset = MediaAsset & {
  asset_id?: string;
};

export type MediaStep = mediaComponents["schemas"]["MediaStepResponse"];
export type MediaStepsResponse =
  mediaComponents["schemas"]["MediaStepsResponse"];
export type MediaStepNode = MediaStep & {
  children?: MediaStepNode[];
  level?: number;
};
export type MediaControl = mediaComponents["schemas"]["MediaControl"];
export type MediaAssetCategory =
  mediaComponents["schemas"]["MediaAssetCategory"];
export type MediaDescriptor = mediaComponents["schemas"]["MediaDescriptor"];
export type MediaProject = mediaComponents["schemas"]["MediaProjectResponse"];
export type MediaProjectListItem =
  mediaComponents["schemas"]["MediaProjectHeader"];
export type MediaProjectResponse =
  mediaComponents["schemas"]["MediaProjectResponse"] & {
    tags?: ProjectTag[];
    media: ProjectMediaResponse[];
  };
export type ProjectStatus = components["schemas"]["ProjectStatus"];
export type ProjectMediaResponse =
  mediaComponents["schemas"]["ProjectMediaResponse"];
export type MediaDocument = mediaComponents["schemas"]["DocumentResponse"];
export type Document = mediaComponents["schemas"]["Document"];
export type Media = mediaComponents["schemas"]["Media"];

export type MediaProjectInstanceResponse =
  mediaComponents["schemas"]["MediaProjectInstanceResponse"];

export type MediaProjectStoryboards =
  mediaComponents["schemas"]["ProjectStoryboardResponse"];

export type MediaProjectPublicationBase =
  mediaComponents["schemas"]["MediaProjectPublicationBase"];
export type PublicationStatusResponse =
  mediaComponents["schemas"]["PublicationStateResponse"];
export type PublicationStatus = mediaComponents["schemas"]["PublicationStatus"];
export type PublicationAction =
  mediaComponents["schemas"]["PublicationActionRequest"];
export type ProjectSettingsRequest =
  mediaComponents["schemas"]["ProjectSettingsRequest"];
export type ProjectInstanceSettingsRequest =
  mediaComponents["schemas"]["ProjectInstanceSettingsRequest"];

export type AssetUploadResponse =
  mediaComponents["schemas"]["AssetUploadResponse"];

export type VoiceProfileResponse = Omit<
  mediaComponents["schemas"]["VoiceProfileResponse"],
  "category"
> & {
  category: VoiceCategory;
};

export type VoiceProfileDetail =
  mediaComponents["schemas"]["VoiceProfileDetail"];
export type VoiceProfileCreationResponse =
  mediaComponents["schemas"]["VoiceProfileCreationResponse"];
export type FaceProfileResponse =
  mediaComponents["schemas"]["FaceProfileResponse"];
export type FaceProfileCreationResponse =
  mediaComponents["schemas"]["FaceProfileCreationResponse"];
export type FaceProfileCreationRequest =
  mediaComponents["schemas"]["FaceProfileCreationRequest"];
export type FaceCategory = mediaComponents["schemas"]["FaceCategory"];
export type VoiceCategory = mediaComponents["schemas"]["VoiceCategory"];
export type PresenterProfileResponse = Omit<
  mediaComponents["schemas"]["PresenterProfileResponse"],
  "voice_profile"
> & {
  voice_profile: VoiceProfileResponse;
};
export type ContactResponse = mediaComponents["schemas"]["ContactResponse"];

export type ToneResponse = mediaComponents["schemas"]["ToneResponse"];

export type StoryboardStatus = mediaComponents["schemas"]["StoryboardStatus"];

// Storyboard types
export type Storyboard = mediaComponents["schemas"]["Storyboard"];
export type StoryboardSection = mediaComponents["schemas"]["Section"];
export type StoryboardSectionScript = StoryboardSection["script"];

// Extend with properties the API returns but aren't yet in the OpenAPI spec
export type ImageElement = mediaComponents["schemas"]["ImageElement"] & {
  asset_id?: string;
};
type SlideExtension = {
  uuid: string;
  section_id?: string;
  placeholders: { image_element?: ImageElement };
};
type RawSlide = mediaComponents["schemas"]["Slides"]["slides"][number];
export type Slide = RawSlide & SlideExtension;
export type Slides = { slides: Slide[] };
export type SlideType = Slide["slide_type"];

export type ProjectSettingsStatus =
  mediaComponents["schemas"]["ProjectSettingsStatus"];
export type PronunciationResponse =
  mediaComponents["schemas"]["PronunciationResponse"];
export type SavePronunciationRequest =
  mediaComponents["schemas"]["SavePronunciationRequest"];

export type ChatMode = mediaComponents["schemas"]["ChatMode"];
export type MediaGenerationMode =
  mediaComponents["schemas"]["MediaGenerationMode"];
export type MediaGenerationSettingsResponse =
  mediaComponents["schemas"]["MediaGenerationSettingsResponse"];
export type CreateMediaGenerationSettingsRequest =
  mediaComponents["schemas"]["CreateMediaGenerationSettingsRequest"];

export type TScript = {
  content: Maybe<string>;
  revisions?: { content: string; prompt: string }[];
  selected: Maybe<boolean>;
};

export type TDetailedSection = {
  topic: string;
  script: Maybe<TScript>;
  scripts?: Maybe<TScript>[];
  id: string;
};

export type TDetailedStoryboard = {
  title: string;
  sections: TDetailedSection[];
};

export type TSectionOutline = {
  section_id: string; // FIXME Maybe<string>;
  topic: string;
  script: Maybe<TScript>;
};

export type TChapters = mediaComponents["schemas"]["ChapterTiming"];

export type SectionUpdate = {
  script?: string;
};

type DisplaySimilarity = {
  score: number;
  precision: number;
  recall: number;
  semantic_similarity: number;
};

type ReadabilityMetrics = {
  score: Maybe<number>;
  ease: string;
  grade_level: string;
};

type DisplayPolarity = {
  score: number;
  negative: number;
  neutral: number;
  positive: number;
};

export type DisplayMetrics = {
  readability: ReadabilityMetrics;
  polarity: DisplayPolarity;
  similarity: DisplaySimilarity;
};

export type CallToAction = mediaComponents["schemas"]["CallToAction"];

export type CallToActions = {
  actions: CallToAction[];
};

type CreateDocumentAssetRequest =
  mediaComponents["schemas"]["CreateDocumentAssetRequest"];
export type CreateMediaAssetRequest =
  mediaComponents["schemas"]["CreateMediaAssetRequest"];
type CreateProjectMediaAssetRequest =
  mediaComponents["schemas"]["CreateProjectMediaAssetRequest"];

export type BrandKit = mediaComponents["schemas"]["BrandKitResponse"];

export type BrandKitAsset = mediaComponents["schemas"]["BrandKitAsset"];
export type CreateBrandKitAssetRequest =
  mediaComponents["schemas"]["CreateBrandKitAssetRequest"];
type UploadDocumentRequest =
  mediaComponents["schemas"]["UploadDocumentRequest"];
type CreateVoiceAssetRequest =
  mediaComponents["schemas"]["CreateVoiceAssetRequest"];
export type CreateUploadableAssetRequest =
  | CreateBrandKitAssetRequest
  | CreateVoiceAssetRequest
  | CreateMediaAssetRequest
  | CreateDocumentAssetRequest
  | UploadDocumentRequest
  | CreateProjectMediaAssetRequest
  | AddAssetToAssetLibraryRequest;
export type BrandKitAssetResponse =
  mediaComponents["schemas"]["BrandKitResponse"]["assets"][number];
export enum AssetSource {
  UIGenerated = "UIGenerated",
}
export type ContactCreationRequest =
  mediaComponents["schemas"]["ContactCreationRequest"];
export type DocumentExtract = {
  document_id: string;
  text: string;
  occurrences: DocumentChunk[];
};

export type DocumentChunk = {
  chunk: string;
  page_number: number;
  coordinates: [number, number, number, number];
};

export type OnboardingAnswer = mediaComponents["schemas"]["OnboardingAnswer"];
export type CreateOrganizationRequest =
  mediaComponents["schemas"]["OrganizationCreationRequest"];
export type UpdateOrganizationRequest =
  mediaComponents["schemas"]["UpdateOrganizationRequest"];
export type Transitions = mediaComponents["schemas"]["Transitions"];
export type PaletteResponse = mediaComponents["schemas"]["PaletteResponse"];
export type ProjectTag = mediaComponents["schemas"]["EntityTag"];

export type DocumentType = mediaComponents["schemas"]["DocumentType"];
export type AssetLibrary = mediaComponents["schemas"]["AssetLibraryResponse"];
export type CreateAssetResponse =
  mediaComponents["schemas"]["AssetUploadResponse"];
type AddAssetToAssetLibraryRequest =
  mediaComponents["schemas"]["AddAssetToAssetLibraryRequest"];

// workaround to keep strong typing of sources
// patch the section_highlights refactored by backend (Florent) because of lint issues
// with SectionHighlights.sources typed to unknown[]
export type SectionHighlights = {
  [key: string]:
    | mediaComponents["schemas"]["SectionHighlights_HtmlHighlightSource_"]
    | mediaComponents["schemas"]["SectionHighlights_PdfHighlightSource_"];
};
export type StoryboardHighlights = Omit<
  | components["schemas"]["Highlights_PdfHighlightSource_"]
  | components["schemas"]["Highlights_HtmlHighlightSource_"],
  "section_highlights"
> & {
  section_highlights: SectionHighlights;
};
// end patch

export type SectionHighlight = mediaComponents["schemas"]["SectionHighlight"];
export type SectionCuepoints =
  mediaComponents["schemas"]["libertify_models__media__cuepoints__CuePoint"];
export type HTMLHighlightsSource =
  mediaComponents["schemas"]["HtmlHighlightSource"];
export type PDFHighlightsSource =
  mediaComponents["schemas"]["PdfHighlightSource"];

export type CuePointsResponse = mediaComponents["schemas"]["CuePointsResponse"];
export type CuePointsRequest = mediaComponents["schemas"]["CuePointsRequest"];
export type ChapterCuePointsResponse =
  mediaComponents["schemas"]["ChapterCuePointsResponse"];

export type LegacyHighlightsConversionRequest =
  mediaComponents["schemas"]["LegacyHighlightsConversionRequest"];
export type LegacyHighlightsConversionResponse = Omit<
  mediaComponents["schemas"]["LegacyHighlightsConversionResponse"],
  "highlights"
> & {
  highlights: StoryboardHighlights;
};

export type StockPhotoResponse =
  mediaComponents["schemas"]["StockPhotoResponse"];
export type StockPhoto = mediaComponents["schemas"]["StockPhoto"];
export type AssetStatusResponse =
  mediaComponents["schemas"]["AssetStatusResponse"][];
export type StockContentRequest =
  mediaComponents["schemas"]["StockContentRequest"];
export type StockVideoResponse =
  mediaComponents["schemas"]["StockVideoResponse"];
export type StockVideo = mediaComponents["schemas"]["StockVideo"];
export type ImageFitStrategy = mediaComponents["schemas"]["ImageFitStrategy"];
export type PublicationMode = mediaComponents["schemas"]["PublicationMode"];

export type DetailedBatchStatus =
  mediaComponents["schemas"]["DetailedBatchStatus"];
export type MediaBatchResponse =
  mediaComponents["schemas"]["MediaBatchResponse"];
export type ImageGenerationEvent =
  mediaComponents["schemas"]["ImageGenerationEvent"];
export type AspectRatio = mediaComponents["schemas"]["AspectRatio"];
export type SlugCheckResponse = mediaComponents["schemas"]["SlugCheckResponse"];
export type MusicOverride = mediaComponents["schemas"]["MusicOverride"];
export type ProjectDocument = mediaComponents["schemas"]["ProjectDocument"];

export type SavedSettingsResponse =
  mediaComponents["schemas"]["SavedSettingsResponse"];
export type SavedSettingsCreationRequest =
  mediaComponents["schemas"]["SavedSettingsCreationRequest"];
export type SavedSettingsUpdateRequest =
  mediaComponents["schemas"]["SavedSettingsUpdateRequest"];
export type StoryboardGenerationMode =
  mediaComponents["schemas"]["StoryboardGenerationMode"];
export type PlaybookStoryboardSettings = NonNullable<
  SavedSettingsUpdateRequest["storyboard_settings"]
>;
export type PlaybookMediaSettings = NonNullable<
  SavedSettingsUpdateRequest["media_settings"]
>;

export type PlaybookResponse = mediaComponents["schemas"]["PlaybookResponse"];
export type PlaybookCreationRequest =
  mediaComponents["schemas"]["PlaybookCreationRequest"];
export type PlaybookUpdateRequest =
  mediaComponents["schemas"]["PlaybookUpdateRequest"];
export type AddOnType = mediaComponents["schemas"]["AddOnType"];
export type AddOnStatus = mediaComponents["schemas"]["AddOnStatus"];
export type ProjectAddOnSettingsPatchRequest =
  mediaComponents["schemas"]["ProjectAddOnSettingsPatchRequest"];
export type ProjectEnabledAddOnsPatchRequest =
  mediaComponents["schemas"]["ProjectEnabledAddOnsPatchRequest"];
